import { ChevronDown, ChevronUp } from "lucide-react";
import { useId, useRef, useState, type ReactNode } from "react";
import { ToolApproval, useToolApproval } from "../../ToolApproval";
import { ToolCallIcon } from "../ToolCallIcon";
import { ToolDetailsPanel } from "../ToolDetailsPanel";
import { ToolStatusChip } from "../ToolStatusChip";

/** Shared edit container; each tool supplies its own comparison. */
export const EditToolCallFrame = ({
  title,
  toolName,
  label,
  approvalTitle = title,
  children,
  controls,
  footer,
  isLoading = false,
  hasError = false,
  isRejected = false,
  hasToolResult = false,
  hasContent = true,
  decision,
}: {
  title: ReactNode;
  toolName: string;
  label: string;
  approvalTitle?: ReactNode;
  children: ReactNode;
  controls?: ReactNode;
  footer?: ReactNode;
  isLoading?: boolean;
  hasError?: boolean;
  isRejected?: boolean;
  hasToolResult?: boolean;
  hasContent?: boolean;
  decision?: "approve" | "reject" | "edit";
}) => {
  const approval = useToolApproval();
  const pending = !!controls && !hasToolResult;
  const [expanded, setExpanded] = useState(true);
  const panelId = useId();
  const cardRef = useRef<HTMLElement>(null);
  const showContent = hasContent && !isRejected;
  const canExpand = showContent && !pending;
  const Chevron = expanded ? ChevronUp : ChevronDown;
  const status =
    isRejected || decision === "reject"
      ? "Rejected"
      : pending
        ? "Needs approval"
        : decision === "approve"
          ? "Approved"
          : hasToolResult && !hasError
            ? "Applied"
            : null;
  const revealChanges = () => {
    setExpanded(true);
    requestAnimationFrame(() => {
      cardRef.current?.scrollIntoView?.({
        behavior: "smooth",
        block: "nearest",
      });
      cardRef.current?.focus({ preventScroll: true });
    });
  };

  return (
    <>
      <article
        ref={cardRef}
        tabIndex={-1}
        aria-label={pending ? `${label} needs approval` : label}
        className={
          pending
            ? "scroll-mt-2 rounded-[3.5px] border border-solid border-[#074ee8] bg-white p-3 text-[13px]"
            : "min-w-0 scroll-mt-2 text-[13px]"
        }
      >
        <header
          className={`group/tool-row relative flex min-h-[30px] min-w-0 items-center gap-[10px] ${pending ? "text-black/[.87]" : "py-1 text-[#6b7280]"}`}
        >
          {canExpand && (
            <button
              type="button"
              aria-label={label}
              aria-expanded={expanded}
              aria-controls={panelId}
              onClick={() => setExpanded(!expanded)}
              className="peer absolute inset-0 w-full cursor-pointer rounded-[3.5px] border-0 bg-transparent p-0 group-hover/tool-row:bg-black/[.04] focus-visible:bg-black/[.04]"
            />
          )}
          <ToolCallIcon
            name={toolName}
            integrationIcon={null}
            active={isLoading}
            failed={hasError}
          />
          <span className="pointer-events-none relative min-w-0 flex-1 truncate [&_button]:pointer-events-auto">
            {title}
          </span>
          {status && <ToolStatusChip status={status} />}
          {hasError && (
            <span className="relative text-xs text-[#b91c1c]">Failed</span>
          )}
          {canExpand && (
            <Chevron
              aria-hidden="true"
              className="pointer-events-none relative h-[14px] w-[14px] shrink-0"
            />
          )}
        </header>
        {showContent && (
          <div
            id={panelId}
            hidden={!pending && !expanded}
            className={pending ? "mt-3" : undefined}
          >
            {pending ? (
              children
            ) : (
              <ToolDetailsPanel label="Tool changes">
                {children}
              </ToolDetailsPanel>
            )}
          </div>
        )}
        {(footer ||
          (pending &&
            approval?.agentName !== "Main agent" &&
            approval?.agentName)) && (
          <footer className="mt-3 flex flex-wrap items-center justify-between gap-2">
            {footer}
            {pending && approval && approval.agentName !== "Main agent" && (
              <span className="text-xs text-[#6b7280]">
                Requested by {approval.agentName}
              </span>
            )}
          </footer>
        )}
      </article>
      {pending &&
        (approval ? (
          <ToolApproval title={approvalTitle} onViewChanges={revealChanges}>
            {controls}
          </ToolApproval>
        ) : (
          <div
            role="group"
            aria-label="Tool approval"
            className="mt-2 flex flex-wrap items-center gap-2"
          >
            {controls}
          </div>
        ))}
    </>
  );
};
